import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { youtubeEmbed } from "@/lib/video";
import { firstName } from "@/lib/utils";
import CustomerProgressBar from "@/components/CustomerProgressBar";
import {
  Wrench as WrenchIcon, AlertTriangle, ShieldCheck, Check, X, Info,
  Loader2, Sparkles, ArrowRight, Video, ChevronDown, ChevronUp, Wallet, Trophy,
  MessageCircle, Send, BellPlus, BellOff, CalendarClock, Download, Trash2,
  PhoneCall, History, ExternalLink, Calendar, Gift, CreditCard, Lock,
} from "lucide-react";
import { toast } from "sonner";

const URGENCY_PILL = {
  green: "bg-success/15 text-success border-success/30",
  amber: "bg-warning/15 text-warning border-warning/30",
  red: "bg-destructive/15 text-destructive border-destructive/30",
};

const URGENCY_LABEL = {
  green: "Good",
  amber: "Advisory",
  red: "Requires attention",
};

// Steve — pre-Arran-meeting (Feb 2026). Explainer videos for aircon and geo/wheel
// alignment work are hidden from the customer view because we don't yet own the
// licence to embed them. Remove `isSuppressedExplainer` calls below the meeting
// to restore.
function isSuppressedExplainer(item) {
  const name = ((item && item.name) || "").toLowerCase();
  return /(aircon|air.?con|air conditioning|geo(metry)?\b|wheel.?align|four.?wheel.?align|4.?wheel.?align|alignment)/i.test(name);
}

function formatVisitDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

/**
 * Render a small subset of markdown (paragraph breaks + **bold**) as safe
 * React nodes, without dangerouslySetInnerHTML. Used for the customer-facing
 * AI explanation on /j/{token}. Keeps the "**Front brakes — urgent:**"
 * headings visually bold rather than showing raw asterisks.
 */
function renderExplanation(text) {
  if (!text) return null;
  const paragraphs = String(text).split(/\n\s*\n+/);
  return paragraphs.map((para, i) => {
    // Split by **bold** while keeping delimiters
    const parts = para.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    const nodes = parts.map((chunk, j) => {
      if (chunk.startsWith("**") && chunk.endsWith("**")) {
        return <strong key={j}>{chunk.slice(2, -2)}</strong>;
      }
      // preserve single line breaks inside a paragraph
      const lines = chunk.split("\n");
      return lines.map((line, k) => (
        <span key={`${j}-${k}`}>
          {line}
          {k < lines.length - 1 && <br />}
        </span>
      ));
    });
    return (
      <p
        key={i}
        className={i > 0 ? "mt-3" : ""}
      >
        {nodes}
      </p>
    );
  });
}

function PreviousVisitBadge({ history }) {
  if (!history) return null;
  const dateLabel = formatVisitDate(history.prior_visit_date);
  const wasDeclined = history.prior_status === "rejected";
  const wasApproved = history.prior_status === "approved";
  const det = history.deterioration;
  const prev = history.prior_measurement;
  const curr = history.current_measurement;

  // Build the human-friendly headline
  let headline;
  if (prev && curr && det !== null && det !== undefined && det > 0) {
    headline = (
      <>
        Previously <span className="font-mono-plex font-bold">{prev.value}{prev.unit}</span>
        {" → now "}
        <span className="font-mono-plex font-bold text-warning">{curr.value}{curr.unit}</span>
        {" ("}
        <span className="text-destructive font-bold">−{det}{prev.unit}</span>
        {" since last visit)"}
      </>
    );
  } else if (prev) {
    headline = (
      <>
        Previously measured at{" "}
        <span className="font-mono-plex font-bold">{prev.value}{prev.unit}</span>
      </>
    );
  } else if (wasDeclined) {
    headline = <>Previously declined{dateLabel ? ` in ${dateLabel}` : ""}</>;
  } else if (wasApproved) {
    headline = <>You authorised this last visit{dateLabel ? ` (${dateLabel})` : ""}</>;
  } else {
    headline = <>We flagged this previously{dateLabel ? ` in ${dateLabel}` : ""}</>;
  }

  // Subtle warmer styling when deterioration is shown (the strongest signal),
  // calmer info-tone when it's just a prior mention.
  const isDeteriorating = det && det > 0;
  const wrapCls = isDeteriorating
    ? "bg-warning/10 border-warning/30 text-warning-foreground"
    : "bg-muted/40 border-border text-muted-foreground";

  return (
    <div
      className={`mt-2 flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-xs ${wrapCls}`}
      data-testid="previous-visit-badge"
    >
      <History className="w-3.5 h-3.5 shrink-0 mt-0.5" />
      <div className="leading-snug">
        <div className="text-[11px] font-bold uppercase tracking-wider opacity-70">
          Your vehicle's history
        </div>
        <div className="mt-0.5">{headline}</div>
        {dateLabel && (
          <div className="opacity-70 mt-0.5">
            {wasDeclined ? "Declined" : wasApproved ? "Authorised" : "Flagged"} {dateLabel}
          </div>
        )}
      </div>
    </div>
  );
}

const PA_TERMS = [6, 9, 10];
const FINANCE_URL = "https://www.bumper.co/for-drivers/pay-later";

// Signed URL for VHC evidence photos — same shared-quote endpoint the videos
// use, so the customer doesn't need to be authenticated to fetch them.
function vhcPhotoUrl(path, token) {
  if (!path) return "";
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const qs = token ? `?share=${encodeURIComponent(token)}` : "";
  return `${base}/api/files/${encodeURIComponent(path)}${qs}`;
}

function VhcPhotoStrip({ photos, itemId, shareToken }) {
  const [lightbox, setLightbox] = useState(null); // path currently zoomed
  if (!photos?.length) return null;
  return (
    <>
      <div className="flex gap-1.5 flex-wrap mt-2" data-testid={`vhc-photos-${itemId}`}>
        {photos.map((p, idx) => (
          <button
            key={p.path || p.id || idx}
            type="button"
            onClick={() => setLightbox(p)}
            className="w-16 h-16 rounded-md overflow-hidden border border-border hover:border-primary transition-colors"
            aria-label={`Open photo ${idx + 1}`}
            data-testid={`vhc-photo-${itemId}-${idx}`}
          >
            <img src={vhcPhotoUrl(p, shareToken)} alt="" className="w-full h-full object-cover" loading="lazy" />
          </button>
        ))}
      </div>
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-background/90 backdrop-blur flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="vhc-photo-lightbox"
        >
          <img src={vhcPhotoUrl(lightbox, shareToken)} alt="" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </>
  );
}

function PayNowCard({ amount, paid, paidAt, busy, polling, onPay, workshopName }) {
  if (paid) {
    const dt = paidAt ? new Date(paidAt) : null;
    const when = dt && !isNaN(dt.getTime())
      ? dt.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
      : "";
    return (
      <div className="card-default p-5 border-success/40 bg-success/5" data-testid="pay-now-paid">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-success text-success-foreground grid place-items-center shrink-0">
            <Check className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="label-overline text-success">Paid in full</div>
            <div className="font-display font-bold text-lg leading-tight">
              £{Number(amount || 0).toFixed(2)} received{when ? ` · ${when}` : ""}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              A receipt has been emailed to you. Just pop in and collect your keys.
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="card-default p-5 border-primary/40" data-testid="pay-now-card">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-md bg-primary/15 text-primary grid place-items-center">
          <CreditCard className="w-4 h-4" />
        </div>
        <div>
          <div className="label-overline text-primary">Pay now</div>
          <div className="font-display font-bold text-lg leading-tight">
            Skip the queue on collection
          </div>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
        Pay <span className="font-semibold text-foreground">£{Number(amount || 0).toFixed(2)}</span> now
        by card and we'll have the paperwork ready — no card taps at the desk when you pick up your car.
      </p>
      <button
        type="button"
        onClick={onPay}
        disabled={busy || polling}
        className="btn-primary w-full mt-4 inline-flex items-center justify-center gap-2 text-sm font-bold rounded-lg px-4 py-3"
        data-testid="pay-now-btn"
      >
        {busy || polling ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
        {polling ? "Confirming payment…" : `Pay £${Number(amount || 0).toFixed(2)} now`}
      </button>
      <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground mt-3">
        <Lock className="w-3 h-3" />
        Secured by Stripe · {workshopName || "Workshop"} never sees your card details
      </div>
    </div>
  );
}

function SpreadCostCard({ amount, compact = false }) {
  return (
    <div className={`card-default p-5 border-primary/40 ${compact ? "" : ""}`} data-testid="spread-cost-card">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-md bg-primary/15 text-primary grid place-items-center">
          <Wallet className="w-4 h-4" />
        </div>
        <div>
          <div className="label-overline text-primary">Spread the cost</div>
          <div className="font-display font-bold text-lg leading-tight">Would you like to spread your payments?</div>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
        Use the link below to spread the cost of your repairs over
        <span className="font-semibold text-foreground"> 6, 9 or 10 months</span> at
        <span className="font-semibold text-foreground"> 0% interest</span>.
      </p>
      {amount > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-4">
          {PA_TERMS.map(t => (
            <div key={t} className="rounded-lg border border-border bg-muted/40 px-2 py-3 text-center" data-testid={`pa-term-${t}`}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{t} months</div>
              <div className="font-display font-bold text-base mt-1">£{(amount / t).toFixed(2)}</div>
              <div className="text-[10px] text-muted-foreground">/ month</div>
            </div>
          ))}
        </div>
      )}
      <a
        href={FINANCE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary w-full mt-4 inline-flex items-center justify-center gap-2 text-sm font-bold rounded-lg px-4 py-3"
        data-testid="spread-cost-link"
      >
        Spread the cost of your repairs
        <ExternalLink className="w-4 h-4" />
      </a>
      <div className="text-[11px] text-muted-foreground mt-3">
        Subject to status. The workshop will confirm eligibility when you collect the vehicle.
      </div>
    </div>
  );
}

function GdprFooter({ token }) {
  const [busy, setBusy] = useState(null); // 'export' | 'erase'
  const [confirming, setConfirming] = useState(false);

  const exportMyData = async () => {
    setBusy("export");
    try {
      const r = await api.get(`/public/jobs/${token}/my-data`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `servicesell-my-data-${token.slice(0, 6)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't export — please try again");
    } finally { setBusy(null); }
  };

  const erase = async () => {
    setBusy("erase");
    try {
      await api.post(`/public/jobs/${token}/erase`);
      toast.success("Personal data erased. Thank you.");
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't erase — please try again");
    } finally { setBusy(null); setConfirming(false); }
  };

  return (
    <footer className="max-w-4xl mx-auto px-5 py-10 mt-10 border-t border-border" data-testid="public-gdpr-footer">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="w-4 h-4 text-primary" />
          <span>Your data, your control. Read our <a href="/privacy" target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid="gdpr-privacy-link">Privacy Policy</a>.</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={exportMyData} disabled={!!busy} className="btn-outline text-xs" data-testid="gdpr-export-btn">
            {busy === "export" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Download my data
          </button>
          {!confirming ? (
            <button type="button" onClick={() => setConfirming(true)} disabled={!!busy} className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1" data-testid="gdpr-erase-btn">
              <Trash2 className="w-3.5 h-3.5" />
              Delete my data
            </button>
          ) : (
            <div className="flex items-center gap-2 text-xs" data-testid="gdpr-erase-confirm">
              <span className="text-muted-foreground">Are you sure?</span>
              <button type="button" onClick={erase} disabled={!!busy} className="px-2 py-1 rounded bg-destructive text-destructive-foreground font-semibold" data-testid="gdpr-erase-confirm-btn">
                {busy === "erase" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Yes, erase"}
              </button>
              <button type="button" onClick={() => setConfirming(false)} className="px-2 py-1 rounded border border-border" data-testid="gdpr-erase-cancel">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Audit/trust banner — visible reassurance for the customer that their
          interaction with this page is logged and protected. Removes the
          unspoken "is this a real garage?" concern, especially on a customer's
          first ServiceSell PRO interaction. */}
      <div className="mt-6 rounded-md border border-border bg-card/40 p-3 flex items-start gap-3" data-testid="public-audit-badge">
        <ShieldCheck className="w-4 h-4 text-success shrink-0 mt-0.5" />
        <div className="flex-1 text-[11px] text-muted-foreground leading-relaxed">
          <span className="font-semibold text-foreground">Every action on this page is timestamped and audit-logged.</span>
          {" "}Your data is encrypted in transit and at rest. Card details, where present, are processed by Stripe and never touch our servers.
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-border/60 text-[10px] text-muted-foreground/70 leading-relaxed" data-testid="public-company-details">
        Platform provided by ServiceSellPro Limited · Registered in England &amp; Wales · Company No. 17211707 · ICO ZC144538 · Registered UK trade mark No. UK00004398263 · Registered office: 25 Meadow Park, Kirkheaton, Huddersfield HD5 0HX
      </div>
    </footer>
  );
}

function AmberSnoozeCard({ item, snooze, busy, onChoose }) {
  const [customOpen, setCustomOpen] = useState(false);
  const [customDate, setCustomDate] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const max = new Date(Date.now() + 365 * 2 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const recommended = item.recommended_in_months ?? null;

  // ----- Customer has already chosen — show summary -----
  if (snooze?.opted_out) {
    return (
      <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3 flex items-center justify-between gap-3" data-testid={`snooze-opted-out-${item.id}`}>
        <div className="flex items-center gap-2 text-sm">
          <BellOff className="w-4 h-4 text-muted-foreground shrink-0" />
          <span>No reminder — got it.</span>
        </div>
        <button type="button" onClick={() => onChoose({ months: 6 })} disabled={busy} className="text-xs text-primary hover:underline" data-testid={`snooze-change-${item.id}`}>
          Change my mind
        </button>
      </div>
    );
  }
  if (snooze?.scheduled_at) {
    const when = new Date(snooze.scheduled_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    return (
      <div className="mt-3 rounded-lg border border-primary/40 bg-primary/5 p-3 flex items-center justify-between gap-3" data-testid={`snooze-scheduled-${item.id}`}>
        <div className="flex items-center gap-2 text-sm">
          <CalendarClock className="w-4 h-4 text-primary shrink-0" />
          <span>We'll text & email you on <span className="font-semibold">{when}</span>.</span>
        </div>
        <button type="button" onClick={() => onChoose({ opt_out: true })} disabled={busy} className="text-xs text-muted-foreground hover:text-destructive" data-testid={`snooze-cancel-${item.id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Cancel"}
        </button>
      </div>
    );
  }

  // ----- Initial chooser -----
  return (
    <div className="mt-3 rounded-lg border border-warning/30 bg-warning/5 p-4 space-y-3" data-testid={`snooze-chooser-${item.id}`}>
      <div className="flex items-start gap-2">
        <BellPlus className="w-4 h-4 text-warning mt-0.5 shrink-0" />
        <div>
          <div className="text-sm font-semibold">Snooze, don't lose it.</div>
          <p className="text-xs text-muted-foreground leading-snug mt-0.5">
            Our technician advised this work could wait. Want us to remind you when it's a good time to revisit?
            Your quote stays live — re-authorise in one tap.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[3, 6, 9].map(m => {
          const isRec = recommended === m;
          return (
            <button
              key={m}
              type="button"
              disabled={busy}
              onClick={() => onChoose({ months: m })}
              className={`relative rounded-lg border bg-background py-2 text-sm font-bold transition-colors disabled:opacity-50 ${
                isRec ? "border-warning bg-warning/15 text-warning ring-2 ring-warning/30 hover:bg-warning/20"
                      : "border-border hover:border-warning hover:bg-warning/10"
              }`}
              data-testid={`snooze-${item.id}-${m}m`}
            >
              {m} months
              {isRec && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded-full bg-warning text-warning-foreground text-[9px] font-bold uppercase tracking-wider whitespace-nowrap">
                  ★ Recommended
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        {!customOpen ? (
          <button type="button" onClick={() => setCustomOpen(true)} className="text-primary hover:underline" data-testid={`snooze-pick-date-${item.id}`}>
            Pick a different date
          </button>
        ) : (
          <div className="flex items-center gap-2 w-full">
            <input
              type="date"
              min={today}
              max={max}
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              className="input-default text-xs flex-1"
              data-testid={`snooze-custom-input-${item.id}`}
            />
            <button
              type="button"
              disabled={!customDate || busy}
              onClick={() => onChoose({ custom_date: customDate })}
              className="btn-primary text-xs disabled:opacity-50"
              data-testid={`snooze-custom-save-${item.id}`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
            </button>
          </div>
        )}
        <button type="button" onClick={() => onChoose({ opt_out: true })} disabled={busy} className="text-muted-foreground hover:text-foreground ml-auto" data-testid={`snooze-no-thanks-${item.id}`}>
          No thanks
        </button>
      </div>
    </div>
  );
}

export default function PublicJobPage() {
  const { token } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const reconsiderItemId = searchParams.get("reconsider");
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decisions, setDecisions] = useState({}); // item_id -> "approved" | "rejected"
  const [tyreChoices, setTyreChoices] = useState({}); // item_id -> "recommended" | "budget"
  const [snoozes, setSnoozes] = useState({});     // item_id -> {months, scheduled_at, opted_out}
  const [snoozing, setSnoozing] = useState(null); // item_id currently saving
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [warning, setWarning] = useState(null); // { item, pitch }
  const [pitchBusy, setPitchBusy] = useState(false);
  const [openExplainer, setOpenExplainer] = useState({}); // item_id -> bool
  // Traffic-light section collapse state — red & amber default-open, green default-closed.
  const [openSection, setOpenSection] = useState({});
  const [reconsidering, setReconsidering] = useState(false);
  const [reconsiderDone, setReconsiderDone] = useState(false);
  const [showTellTech, setShowTellTech] = useState(false);
  const [tellTechText, setTellTechText] = useState("");
  const [tellTechBusy, setTellTechBusy] = useState(false);
  const [tellTechSent, setTellTechSent] = useState(false);
  const [aiExpanded, setAiExpanded] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const videoElRef = useRef(null);
  const [done, setDone] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [payStatus, setPayStatus] = useState(null); // "polling" | "paid" | "failed"
  const paidSessionId = searchParams.get("paid_session");
  const payCancelled = searchParams.get("pay_cancelled");

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/public/jobs/${token}`);
      setJob(r.data);
      const d = {};
      (r.data.items || []).forEach(i => {
        if (i.status !== "pending") d[i.id] = i.status;
      });
      setDecisions(d);
      // Hydrate tyre tier choices — server-stored choice wins; default to
      // "recommended" when an alternative is offered but no choice made yet.
      const tc = {};
      (r.data.items || []).forEach(i => {
        if (i.tyre_budget) {
          tc[i.id] = i.tyre_choice || "recommended";
        }
      });
      setTyreChoices(tc);
      // Hydrate snooze state from any pre-existing reminder choices on the job
      const sn = {};
      (r.data.items || []).forEach(i => {
        if (i.reminder_opted_out) sn[i.id] = { opted_out: true };
        else if (i.reminder_scheduled_at) sn[i.id] = { months: i.reminder_months, scheduled_at: i.reminder_scheduled_at };
      });
      setSnoozes(sn);
      setComment(r.data.customer_comment || "");
      if (["approved", "partial", "rejected"].includes(r.data.status) && r.data.responded_at) {
        setDone(true);
      }
    } catch (e) {
      toast.error("Quote not found or not yet shared");
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { refresh(); }, [refresh]);

  // Handle return from Stripe checkout — either success (poll status) or cancel (toast).
  useEffect(() => {
    if (payCancelled) {
      toast.info("Payment cancelled — you can try again from the button below.");
      searchParams.delete("pay_cancelled");
      setSearchParams(searchParams, { replace: true });
      return;
    }
    if (!paidSessionId) return;
    let cancelled = false;
    let attempts = 0;
    setPayStatus("polling");
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/public/jobs/${token}/pay/status/${paidSessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setPayStatus("paid");
          toast.success("Payment received — thank you.");
          searchParams.delete("paid_session");
          setSearchParams(searchParams, { replace: true });
          await refresh();
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) {
          setPayStatus("failed");
          toast.error("Payment didn't complete. Please try again.");
          return;
        }
        if (attempts < 15) setTimeout(poll, 2000);
        else setPayStatus(null);
      } catch (e) {
        if (attempts < 15) setTimeout(poll, 2000);
        else setPayStatus(null);
      }
    };
    poll();
    return () => { cancelled = true; };
    // searchParams is mutated imperatively inside; re-running on its identity would restart the poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidSessionId, payCancelled, token, refresh]);

  const payInFull = async () => {
    setPayBusy(true);
    try {
      const r = await api.post(`/public/jobs/${token}/pay/checkout`, {
        origin_url: window.location.origin,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
        return;
      }
      toast.error("Couldn't start payment — please try again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start payment — please try again.");
    } finally {
      setPayBusy(false);
    }
  };

  const snoozeItem = async (itemId, choice) => {
    // choice: {months: 3|6|9} | {custom_date: 'YYYY-MM-DD'} | {opt_out: true}
    setSnoozing(itemId);
    try {
      const r = await api.post(`/public/jobs/${token}/items/${itemId}/snooze`, choice);
      setSnoozes((prev) => ({ ...prev, [itemId]: r.data.opt_out
        ? { opted_out: true }
        : { months: r.data.months, scheduled_at: r.data.reminder_scheduled_at } }));
      toast.success(r.data.opt_out
        ? "Got it — no reminder."
        : `We'll remind you on ${new Date(r.data.reminder_scheduled_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save — please try again");
    } finally { setSnoozing(null); }
  };

  const decide = async (item, status) => {
    setDecisions(prev => ({ ...prev, [item.id]: status }));
    if (status === "rejected" && item.urgency === "red") {
      const fallback =
        `Declining ${item.name} can have real safety consequences. ` +
        `For RED-rated work like this, even a short delay can lead to bigger repairs or risk on the road. ` +
        `No pressure either way — just wanted to make sure you had the full picture.`;
      // Show the modal immediately with the fallback so there's never a blank
      // screen, then upgrade to the AI-generated explanation when it arrives.
      setWarning({ item, pitch: fallback, ai_pending: true });
      setPitchBusy(true);
      try {
        const r = await api.post(`/public/jobs/${token}/items/${item.id}/customer-explainer`, {});
        const aiText = (r?.data?.explanation || "").trim();
        if (aiText) {
          setWarning(w => (w && w.item.id === item.id ? { ...w, pitch: aiText, ai_pending: false } : w));
        } else {
          setWarning(w => (w && w.item.id === item.id ? { ...w, ai_pending: false } : w));
        }
      } catch {
        // Stick with the fallback — public endpoint, fail soft for the customer
        setWarning(w => (w && w.item.id === item.id ? { ...w, ai_pending: false } : w));
      } finally {
        setPitchBusy(false);
      }
    }
  };

  const requestCallback = async (item, preferredTime = "asap") => {
    setDecisions(prev => ({ ...prev, [item.id]: "needs_advice" }));
    try {
      await api.post(`/public/jobs/${token}/callback`, {
        item_ids: [item.id],
        preferred_time: preferredTime,
        phone: job?.customer_phone || "",
        customer_note: "",
      });
      toast.success("Got it — your advisor will be in touch shortly", { duration: 5000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't log your request — please try again");
      // Revert the local pill so they can try again
      setDecisions(prev => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    }
  };

  const sendTellTech = async () => {
    const text = (tellTechText || "").trim();
    if (!text) return;
    setTellTechBusy(true);
    try {
      await api.post(`/public/jobs/${token}/messages`, { text });
      setTellTechSent(true);
      setTellTechText("");
      toast.success("Sent to your technician");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send — please try again");
    } finally { setTellTechBusy(false); }
  };

  const handleReconsider = async () => {
    if (!reconsiderItemId) return;
    setReconsidering(true);
    try {
      await api.post(`/public/jobs/${token}/items/${reconsiderItemId}/reconsider`, {});
      setReconsiderDone(true);
      toast.success("Thanks — we'll book that work in shortly");
      const params = new URLSearchParams(searchParams);
      params.delete("reconsider");
      setSearchParams(params, { replace: true });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not authorise — please try again");
    } finally { setReconsidering(false); }
  };

  // Auto-scroll to the reconsider item once the job loads
  useEffect(() => {
    if (!loading && job && reconsiderItemId && !reconsiderDone) {
      const el = document.getElementById(`item-${reconsiderItemId}`);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 350);
      }
    }
  }, [loading, job, reconsiderItemId, reconsiderDone]);

  const submit = async () => {
    if (!job?.items?.length) return;
    const actionable = job.items.filter(i => i.urgency !== "green" && i.type !== "accessory");
    const unanswered = actionable.filter(i => !decisions[i.id]);
    if (unanswered.length) {
      toast.error(`Please decide on all ${actionable.length} amber/red items`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: job.items.map(i => {
          const entry = {
            item_id: i.id,
            status: i.type === "accessory"
              ? (decisions[i.id] || "rejected")           // optional extras — "no thanks" by default
              : i.urgency === "green" ? "approved" : decisions[i.id],
            customer_note: "",
          };
          if (i.tyre_budget) entry.tyre_choice = tyreChoices[i.id] || "recommended";
          return entry;
        }),
        overall_comment: comment,
      };
      const r = await api.post(`/public/jobs/${token}/respond`, payload);
      setJob(r.data);
      setDone(true);
      toast.success("Thank you — we received your reply");
    } catch (e) { toast.error("Failed to submit"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!job) return <div className="min-h-screen grid place-items-center text-muted-foreground">Quote not available</div>;

  // Treat known placeholder/garbage paths as "no video"
  const hasRealVideo = job.video_path && job.video_path.length > 2 && job.video_path !== "x";
  const videoSrc = hasRealVideo ? `${API}/files/${job.video_path}?share=${encodeURIComponent(job.share_token)}` : null;
  const subtotal = job.subtotal || 0;
  const reconsiderItem = reconsiderItemId ? (job.items || []).find(i => i.id === reconsiderItemId) : null;
  // Effective unit price honours the customer's tier choice on tyre items.
  // Falls back to the server-stored value when no offering exists.
  const effectivePrice = (it) => {
    if (it.tyre_budget && tyreChoices[it.id] === "budget") {
      return Number(it.tyre_budget.price || 0);
    }
    return Number(it.unit_price || 0);
  };
  const effectiveName = (it) => {
    if (it.tyre_budget && tyreChoices[it.id] === "budget") {
      return it.tyre_budget.name || it.name;
    }
    return it.name;
  };

  const approvedItems = (job.items || []).filter(i => decisions[i.id] === "approved");
  const approvedTotal = approvedItems.reduce((s, i) => s + Number(i.quantity) * effectivePrice(i), 0);
  const rebookItems = (job.items || []).filter(i => decisions[i.id] === "rebook");
  const rebookTotal = rebookItems.reduce((s, i) => s + Number(i.quantity) * effectivePrice(i), 0);
  const hasAnyDecision = Object.values(decisions).some(Boolean);

  // Render-helper used by the traffic-light grouped sections below.
  // Defined here (vs above the return) so it has closure over decisions/openExplainer/snoozes etc.
  const renderItem = (it) => {
    const d = decisions[it.id];
    const isGreen = it.urgency === "green";
    return (
      <li
        key={it.id}
        id={`item-${it.id}`}
        className={`p-5 transition-colors scroll-mt-24 ${
          reconsiderItemId === it.id && !reconsiderDone ? "ring-2 ring-primary/60 bg-primary/5" :
          d === "approved" ? "bg-success/[0.06]" :
          d === "rejected" ? "bg-destructive/[0.06]" :
          d === "rebook" || d === "needs_advice" ? "bg-primary/[0.06]" : ""
        }`}
        data-testid={`public-item-${it.id}`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-md grid place-items-center shrink-0 ${
            it.urgency === "green" ? "bg-success/20 text-success" :
            it.urgency === "amber" ? "bg-warning/20 text-warning" :
            "bg-destructive/20 text-destructive"
          }`}>
            <WrenchIcon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="font-semibold">{it.name}</div>
              {it.urgency === "red" && (
                <span className={`text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${URGENCY_PILL[it.urgency]}`}>
                  <AlertTriangle className="w-3 h-3 inline mr-0.5" />
                  {URGENCY_LABEL[it.urgency]}
                </span>
              )}
            </div>
            {it.description && <div className="text-sm text-muted-foreground mt-1">{it.description}</div>}
            {Array.isArray(it.photo_urls) && it.photo_urls.length > 0 && (
              <VhcPhotoStrip photos={it.photo_urls} itemId={it.id} shareToken={token} />
            )}
            <PreviousVisitBadge history={it.history} />
            {it.explainer_url && youtubeEmbed(it.explainer_url) && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setOpenExplainer(o => ({ ...o, [it.id]: !o[it.id] }))}
                  className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary hover:underline"
                  data-testid={`explainer-toggle-${it.id}`}
                >
                  <Video className="w-3.5 h-3.5" />
                  {openExplainer[it.id] ? "Hide explainer video" : "Watch explainer video"}
                  {openExplainer[it.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                {openExplainer[it.id] && (
                  <div className="mt-2 aspect-video rounded-lg overflow-hidden border border-border bg-foreground" data-testid={`explainer-iframe-${it.id}`}>
                    <iframe
                      src={youtubeEmbed(it.explainer_url)}
                      title={`Explainer: ${it.name}`}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
            )}
            {/*
              Non-YouTube explainer link (e.g. Vehicle Visuals `srvc.video`
              short-URLs, Vimeo, dealer-hosted MP4s). We can't safely iframe
              these — most providers set X-Frame-Options: SAMEORIGIN — so we
              render a prominent "Watch video" card that opens in a new tab.
            */}
            {it.explainer_url && !youtubeEmbed(it.explainer_url) && (
              <a
                href={it.explainer_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 transition-colors group"
                data-testid={`explainer-external-${it.id}`}
              >
                <Video className="w-4 h-4 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold">Watch: {it.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Opens in a new tab</div>
                </div>
                <ChevronDown className="w-4 h-4 -rotate-90 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
              </a>
            )}
            {it.explainer_url && job.video_partner === "vehicle_visuals" && (
              <div className="mt-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5" data-testid={`vv-credit-${it.id}`}>
                <Video className="w-3 h-3" /> 3D explainer by <span className="font-bold text-[#7F95E8]">Vehicle Visuals</span>
              </div>
            )}
            {!isGreen && it.tyre_budget && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`tyre-tier-chooser-${it.id}`}>
                {[
                  { key: "recommended", label: "Recommended", name: it.name, price: Number(it.unit_price || 0), pillCls: "bg-primary/15 text-primary border-primary/30", sub: "Mid-range · long-life" },
                  { key: "budget", label: "Budget", name: it.tyre_budget.name, price: Number(it.tyre_budget.price || 0), pillCls: "bg-muted text-foreground border-border", sub: "Cheaper alternative" },
                ].map((opt) => {
                  const active = (tyreChoices[it.id] || "recommended") === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setTyreChoices(prev => ({ ...prev, [it.id]: opt.key }))}
                      className={`text-left rounded-lg border-2 px-3 py-2.5 transition-all ${
                        active ? "border-primary bg-primary/[0.06] ring-2 ring-primary/20" : "border-border hover:border-primary/40 hover:bg-primary/[0.03]"
                      }`}
                      data-testid={`tyre-tier-${opt.key}-${it.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${opt.pillCls}`}>{opt.label}</span>
                        <span className="text-base font-bold font-mono-plex">£{opt.price.toFixed(2)}</span>
                      </div>
                      <div className="mt-1 text-sm font-semibold">{opt.name}</div>
                      <div className="text-[11px] text-muted-foreground">{opt.sub}</div>
                    </button>
                  );
                })}
              </div>
            )}
            {!isGreen && !it.tyre_budget && (
              <>
                <div className="text-sm font-mono-plex mt-2">
                  {it.covered_by_plan ? (
                    <span data-testid={`pub-item-covered-${it.id}`}>
                      {it.quantity} × <span className="line-through opacity-70">£{Number(it.covered_by_plan.original_price || 0).toFixed(2)}</span> ·
                      <span className="font-bold text-success ml-1">£0.00</span>
                    </span>
                  ) : (
                    <>
                      {it.quantity} × £{Number(it.unit_price).toFixed(2)} ·
                      <span className="font-bold ml-1">£{(it.quantity * it.unit_price).toFixed(2)}</span>
                    </>
                  )}
                </div>
                {!it.covered_by_plan && (
                  <div
                    className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground"
                    title="This is the all-in fitted price — parts, labour and VAT included. Nothing else to pay at collection for this item."
                    data-testid={`fitted-price-note-${it.id}`}
                  >
                    <Info className="w-3 h-3" />
                    <span>Fitted price · parts, labour &amp; VAT included</span>
                  </div>
                )}
              </>
            )}
            {it.covered_by_plan && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-success bg-success/10 border border-success/30 px-2 py-0.5 rounded">
                <ShieldCheck className="w-3.5 h-3.5" />
                Covered by your service plan
              </div>
            )}
            {!isGreen && it.tyre_budget && (
              <>
                <div className="text-xs text-muted-foreground mt-2 font-mono-plex">
                  {it.quantity} × £{effectivePrice(it).toFixed(2)} ·
                  <span className="font-bold text-foreground ml-1">£{(it.quantity * effectivePrice(it)).toFixed(2)}</span>
                </div>
                <div
                  className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground"
                  title="This is the all-in fitted price — parts, labour and VAT included. Nothing else to pay at collection for this item."
                  data-testid={`fitted-price-note-tyre-${it.id}`}
                >
                  <Info className="w-3 h-3" />
                  <span>Fitted price · parts, labour &amp; VAT included</span>
                </div>
              </>
            )}
            {isGreen && (
              <div className="inline-flex items-center gap-1.5 mt-2 text-success text-sm font-semibold">
                <Check className="w-4 h-4" /> Inspected — no action required
              </div>
            )}
          </div>
        </div>
        {!isGreen && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mt-4">
            <button onClick={() => decide(it, "approved")} className={`px-3 py-2 rounded-lg border text-sm font-bold transition-all flex items-center justify-center gap-2 ${
              d === "approved" ? "border-success bg-success text-success-foreground" : "border-border hover:bg-success/10 hover:border-success/30"
            }`} data-testid={`approve-item-${it.id}`}>
              <Check className="w-4 h-4" /> Approve
            </button>
            <button onClick={() => decide(it, "rebook")} className={`px-3 py-2 rounded-lg border text-sm font-bold transition-all flex items-center justify-center gap-2 ${
              d === "rebook" ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-primary/10 hover:border-primary/30"
            }`} data-testid={`rebook-item-${it.id}`}>
              <Calendar className="w-4 h-4" /> Book another day
            </button>
            <button onClick={() => requestCallback(it)} className={`px-3 py-2 rounded-lg border text-sm font-bold transition-all flex items-center justify-center gap-2 ${
              d === "needs_advice" ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-primary/10 hover:border-primary/30"
            }`} data-testid={`callback-item-${it.id}`}>
              <PhoneCall className="w-4 h-4" /> Speak to advisor
            </button>
            <button onClick={() => decide(it, "rejected")} className={`px-3 py-2 rounded-lg border text-sm font-bold transition-all flex items-center justify-center gap-2 ${
              d === "rejected" ? "border-destructive bg-destructive text-destructive-foreground" : "border-border hover:bg-destructive/10 hover:border-destructive/30"
            }`} data-testid={`reject-item-${it.id}`}>
              <X className="w-4 h-4" /> Decline
            </button>
          </div>
        )}
        {d === "needs_advice" && (
          <div className="mt-3 rounded-lg bg-primary/5 border border-primary/30 p-3 text-xs flex items-start gap-2" data-testid={`callback-confirmation-${it.id}`}>
            <PhoneCall className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <div>
              <strong className="text-primary">Callback requested.</strong>{" "}
              Your advisor has been alerted and will ring you about this item shortly.
            </div>
          </div>
        )}
        {d === "rebook" && (
          <div className="mt-3 rounded-lg bg-primary/5 border border-primary/30 p-3 text-xs flex items-start gap-2" data-testid={`rebook-confirmation-${it.id}`}>
            <Calendar className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <div>
              <strong className="text-primary">We&rsquo;ll get you rebooked.</strong>{" "}
              Your advisor has been notified and will contact you to arrange a return visit that suits.
            </div>
          </div>
        )}
        {it.urgency === "amber" && d === "rejected" && (
          <AmberSnoozeCard
            item={it}
            snooze={snoozes[it.id]}
            busy={snoozing === it.id}
            onChoose={(choice) => snoozeItem(it.id, choice)}
          />
        )}
      </li>
    );
  };

  if (job.status === "completed") {
    const tyEmbed = youtubeEmbed(job.thank_you_url);
    return (
      <div className="min-h-screen bg-background customer-view">
        <header className="border-b border-border bg-card">
          <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between">
            <div>
              <div className="label-overline">{job.workshop_name || "Workshop"}</div>
              <div className="font-display font-bold text-lg leading-tight">All done</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Hi,</div>
              <div className="font-semibold">{job.customer_name || "Customer"}</div>
            </div>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-5 py-6 space-y-6" data-testid="public-completed">
          <CustomerProgressBar job={job} />
          <div className="card-default overflow-hidden">
            <div className="p-6 text-center bg-success/10 border-b border-border">
              <div className="w-14 h-14 rounded-full bg-success grid place-items-center mx-auto">
                <Trophy className="w-7 h-7 text-success-foreground" />
              </div>
              <div className="label-overline mt-5 text-success">Job complete</div>
              <h1 className="font-display text-3xl font-bold tracking-tight mt-2">
                Thank you, {firstName(job.customer_name)}.
              </h1>
              <p className="text-muted-foreground mt-2 max-w-md mx-auto leading-relaxed">
                Your {[job.vehicle_year, job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ") || "vehicle"} is ready and the work is signed off. We've put together a short message for you.
              </p>
            </div>
            {tyEmbed && (
              <div className="p-1 bg-foreground">
                <div className="aspect-video">
                  <iframe
                    src={tyEmbed}
                    title="Thank you from your workshop"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    data-testid="thankyou-video"
                  />
                </div>
              </div>
            )}
            <div className="p-5 border-t border-border">
              <div className="label-overline">Work completed</div>
              <ul className="mt-2 space-y-1">
                {(job.booked_service_amount || 0) > 0 && (
                  <li className="flex justify-between text-sm">
                    <span className="text-foreground/90">{job.booked_service_name || "Booked service"}</span>
                    <span className="font-mono-plex text-muted-foreground">£{Number(job.booked_service_amount || 0).toFixed(2)}</span>
                  </li>
                )}
                {(job.items || []).filter(i => i.status === "approved" && i.urgency !== "green").map(i => (
                  <li key={i.id} className="flex justify-between text-sm">
                    <span className="text-foreground/90">{i.name}</span>
                    <span className="font-mono-plex text-muted-foreground">£{(Number(i.quantity) * Number(i.unit_price)).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-border">
                <span className="label-overline">Total paid</span>
                <span className="font-display font-bold text-2xl tracking-tight">
                  £{(Number(job.booked_service_amount || 0) + Number(job.approved_total || 0)).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Customer satisfaction survey */}
          {job.survey_url && (
            <div className="card-default p-5 border-primary/40 bg-primary/5" data-testid="public-survey-card">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="label-overline text-primary">A quick favour</div>
                  <div className="font-display font-bold text-lg mt-1">How did we do?</div>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Would you mind taking 60 seconds to tell us about your experience today? Your feedback shapes how we look after every customer who walks through our doors.
                  </p>
                  <a
                    href={job.survey_url}
                    target="_blank" rel="noreferrer"
                    className="btn-primary text-sm mt-4 w-fit"
                    data-testid="public-survey-cta"
                  >
                    Leave feedback <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Handover checklist (subtle reassurance) */}
          {job.handover && (job.handover.washed || job.handover.vacuumed || job.handover.interior_clean || job.handover.keys_returned) && (
            <div className="card-default p-5" data-testid="public-handover-card">
              <div className="label-overline">Before we handed your keys back</div>
              <ul className="mt-3 grid grid-cols-2 gap-2">
                {[
                  ["washed", "Washed"],
                  ["vacuumed", "Vacuumed"],
                  ["interior_clean", "Interior wiped down"],
                  ["keys_returned", "Final inspection"],
                ].filter(([k]) => job.handover[k]).map(([k, label]) => (
                  <li key={k} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-success" />
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="text-center text-xs text-muted-foreground py-4">
            Powered by ServiceSell Pro · Inspect · Advise · Sell · Grow
          </div>
        </main>
      </div>
    );
  }

  if (done && !reconsiderItemId) {
    const allApproved = job.status === "approved";
    return (
      <div className="min-h-screen bg-background customer-view">
        <div className="max-w-2xl mx-auto px-6 py-10 space-y-6" data-testid="public-thankyou">
          {/* Where's my car? — same 5-stage bar even after approval */}
          <CustomerProgressBar job={job} />
          <div className="card-default overflow-hidden">
            <div className={`p-8 text-center ${allApproved ? "bg-success/5" : "bg-primary/5"}`}>
              <div className={`w-14 h-14 rounded-full grid place-items-center mx-auto ${allApproved ? "bg-success" : "bg-primary"}`}>
                {allApproved ? <Check className="w-7 h-7 text-white" /> : <ShieldCheck className="w-7 h-7 text-white" />}
              </div>
              <h1 className="font-display text-3xl font-bold tracking-tight mt-5">Thank you, {firstName(job.customer_name)}</h1>
              <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                {allApproved
                  ? "Your approval has been sent to the workshop. They'll be in touch shortly to schedule the work."
                  : "Your response has been sent to the workshop. They'll be in touch shortly."}
              </p>
            </div>
            <div className="p-6 border-t border-border space-y-2">
              {(job.booked_service_amount || 0) > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{job.booked_service_name || "Booked service"}</span>
                    <span className="font-mono-plex">£{Number(job.booked_service_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Additional work approved</span>
                    <span className="font-mono-plex">£{(job.approved_total || 0).toFixed(2)}</span>
                  </div>
                  {job.manual_discount && Number(job.manual_discount.amount) > 0 && (
                    <div className="flex justify-between text-sm text-warning" data-testid="thankyou-discount-row">
                      <span>
                        {job.manual_discount.pct}% discount
                        {job.manual_discount.reason ? ` — ${job.manual_discount.reason}` : ""}
                      </span>
                      <span className="font-mono-plex">− £{Number(job.manual_discount.amount).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-border pt-2 flex justify-between items-baseline">
                    <div className="label-overline">Total to pay on collection</div>
                    <div className="font-display font-bold text-3xl" data-testid="thankyou-grand-total">
                      £{Number(job.grand_total ?? (Number(job.booked_service_amount || 0) + Number(job.approved_total || 0))).toFixed(2)}
                    </div>
                  </div>
                </>
              )}
              {!(job.booked_service_amount > 0) && (
                <>
                  <div className="label-overline">Approved total</div>
                  <div className="font-display font-bold text-3xl mt-1">£{(job.approved_total || 0).toFixed(2)}</div>
                </>
              )}
            </div>
          </div>
          {((Number(job.booked_service_amount || 0) + Number(job.approved_total || 0)) > 0) && (
            <PayNowCard
              amount={Number(job.grand_total ?? (Number(job.booked_service_amount || 0) + Number(job.approved_total || 0)))}
              paid={job.payment_status === "paid" || payStatus === "paid"}
              paidAt={job.paid_at}
              busy={payBusy}
              polling={payStatus === "polling"}
              onPay={payInFull}
              workshopName={job.workshop_name}
            />
          )}
          {((Number(job.booked_service_amount || 0) + Number(job.approved_total || 0)) > 0) && (
            <SpreadCostCard amount={Number(job.booked_service_amount || 0) + Number(job.approved_total || 0)} />
          )}
          <div className="card-default p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/15 grid place-items-center shrink-0">
                <MessageCircle className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <div className="font-display font-bold text-lg">Forgot something?</div>
                <p className="text-sm text-muted-foreground mt-1">If anything else comes to mind — a noise, a smell, a question — drop a quick message and we'll pick it up before your car goes out.</p>
                <button onClick={() => { setShowTellTech(true); setTellTechSent(false); }} className="btn-primary text-sm mt-4" data-testid="thankyou-tell-tech-btn">
                  <MessageCircle className="w-4 h-4" /> Tell my technician
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Same modal — works on the thank-you state too */}
        {showTellTech && (
          <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" onClick={() => setShowTellTech(false)} data-testid="tell-tech-modal">
            <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border w-full sm:max-w-md rounded-t-2xl sm:rounded-lg overflow-hidden animate-fade-up">
              {!tellTechSent ? (
                <>
                  <div className="flex items-start justify-between p-5 border-b border-border">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/15 grid place-items-center shrink-0">
                        <MessageCircle className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <div className="label-overline">Quick message</div>
                        <h3 className="font-display font-bold text-lg mt-0.5 leading-tight">Tell your technician</h3>
                        <p className="text-xs text-muted-foreground mt-1">Anything goes. Goes straight to the workshop.</p>
                      </div>
                    </div>
                    <button onClick={() => setShowTellTech(false)} className="btn-outline" data-testid="tell-tech-close-btn"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="p-5 space-y-3">
                    <textarea value={tellTechText} onChange={(e) => setTellTechText(e.target.value)} rows={5} maxLength={1000} placeholder="e.g. The boot lock has been sticking sometimes — could you have a look?" className="input-default text-sm" autoFocus data-testid="tell-tech-input" />
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-mono-plex text-muted-foreground">{tellTechText.length} / 1000</span>
                      <button type="button" onClick={sendTellTech} disabled={tellTechBusy || !tellTechText.trim()} className="btn-primary text-sm" data-testid="tell-tech-send-btn">
                        {tellTechBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Send
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-8 text-center" data-testid="tell-tech-sent">
                  <div className="w-12 h-12 rounded-full bg-success text-success-foreground grid place-items-center mx-auto"><Check className="w-6 h-6" /></div>
                  <h3 className="font-display font-bold text-xl mt-4">Got it — your technician will see it.</h3>
                  <div className="mt-5 flex gap-2 justify-center">
                    <button type="button" onClick={() => setTellTechSent(false)} className="btn-outline text-sm" data-testid="tell-tech-another-btn">Send another</button>
                    <button type="button" onClick={() => setShowTellTech(false)} className="btn-primary text-sm" data-testid="tell-tech-done-btn">Done</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background customer-view">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between">
          <div>
            <div className="label-overline">{job.workshop_name || "Workshop"}</div>
            <div className="font-display font-bold text-lg leading-tight">A quote for you</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground">Hi,</div>
            <div className="font-semibold">{job.customer_name || "Customer"}</div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6 space-y-6">
        {/* 5-stage progress bar — where's my car? */}
        <CustomerProgressBar job={job} />

        {/* Estimated ready time — kills the "when will it be done?" anxiety */}
        {job.estimated_ready_text && (
          <div className="card-default p-4 border-primary/40 bg-primary/[0.04] flex items-center gap-3" data-testid="customer-ready-by">
            <div className="w-10 h-10 rounded-full bg-primary/15 text-primary grid place-items-center shrink-0">
              <CalendarClock className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="label-overline text-primary">Ready by</div>
              <div className="font-display font-bold text-lg leading-tight mt-0.5" data-testid="customer-ready-by-text">{job.estimated_ready_text}</div>
              <div className="text-xs text-muted-foreground mt-0.5">We'll text you the moment it's done.</div>
            </div>
          </div>
        )}

        {/* Follow-up reconsider banner */}
        {reconsiderItem && !reconsiderDone && reconsiderItem.status !== "approved" && (
          <div className="card-default border-primary bg-primary/5 p-5 animate-fade-up" data-testid="reconsider-banner">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="label-overline text-primary">A quick follow-up</div>
                <div className="font-display font-bold text-lg mt-1 leading-tight">
                  About your {reconsiderItem.name}
                </div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  Our technician flagged this as urgent. If you've changed your mind, you can authorise it in one tap below — no need to call.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={reconsidering}
                    onClick={handleReconsider}
                    className="btn-primary"
                    data-testid="reconsider-authorise-btn"
                  >
                    {reconsidering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Authorise this work
                  </button>
                  {reconsiderItem.explainer_url && !isSuppressedExplainer(reconsiderItem) && (
                    <a
                      href={reconsiderItem.explainer_url}
                      target="_blank" rel="noreferrer"
                      className="btn-outline"
                      data-testid="reconsider-watch-btn"
                    >
                      <Video className="w-4 h-4" /> Watch explainer
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {reconsiderDone && (
          <div className="card-default border-success bg-success/5 p-5" data-testid="reconsider-done-banner">
            <div className="flex items-center gap-3">
              <Check className="w-5 h-5 text-success" />
              <div className="font-semibold">Authorised — thank you. We'll be in touch to schedule.</div>
            </div>
          </div>
        )}

        {/* Video */}
        {videoSrc && (
          <div className="card-default overflow-hidden shadow-lg">
            <video
              ref={(el) => { videoElRef.current = el; }}
              src={videoSrc}
              controls
              poster="https://images.unsplash.com/photo-1596986952526-3be237187071?auto=format&fit=crop&w=1400&q=80"
              className="w-full aspect-video bg-foreground"
              data-testid="public-video"
            />
            <div className="p-4 border-t border-border">
              <div className="label-overline">Inspection video</div>
              <div className="font-display font-bold text-lg mt-1">From {job.technician_name || "your technician"}</div>
              <div className="text-xs text-muted-foreground font-mono-plex mt-1">
                {[job.vehicle_year, job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ")} {job.vehicle_reg ? `· ${job.vehicle_reg}` : ""}
              </div>
              {(job.video_chapters || []).length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Jump to</div>
                  <div className="flex flex-wrap gap-1.5">
                    {job.video_chapters.map((c, i) => (
                      <button
                        key={`${c.ts ?? i}-${c.label || i}`}
                        type="button"
                        onClick={() => {
                          if (videoElRef.current) {
                            videoElRef.current.currentTime = c.ts || 0;
                            videoElRef.current.play().catch(() => {});
                          }
                        }}
                        className="text-xs px-2 py-1 rounded bg-muted border border-border hover:border-primary hover:text-primary transition-colors"
                        data-testid={`chapter-jump-${i}`}
                      >
                        <span className="font-mono-plex text-muted-foreground mr-1">{String(Math.floor(c.ts/60)).padStart(2,"0")}:{String(c.ts%60).padStart(2,"0")}</span>
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CitNOW video (when no uploaded video, or in addition) */}
        {!videoSrc && job.citnow_video_url && (
          <div className="card-default overflow-hidden shadow-lg" data-testid="public-citnow-card">
            <div className="aspect-video bg-foreground">
              <iframe
                src={job.citnow_video_url}
                title="Inspection video"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="public-citnow-iframe"
              />
            </div>
            <div className="p-4 border-t border-border">
              <div className="label-overline">Inspection video</div>
              <div className="font-display font-bold text-lg mt-1">From {job.technician_name || "your technician"}</div>
              <div className="text-xs text-muted-foreground font-mono-plex mt-1">
                {[job.vehicle_year, job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ")} {job.vehicle_reg ? `· ${job.vehicle_reg}` : ""}
              </div>
              <a href={job.citnow_video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-primary hover:underline" data-testid="public-citnow-open">
                <Video className="w-3 h-3" /> Open in CitNOW
              </a>
            </div>
          </div>
        )}

        {/* AI Explanation — collapsible to keep mobile view clean */}
        {job.ai_explanation && (() => {
          // Show short preview (first paragraph or ~180 chars) by default;
          // full message behind a single tap. Keeps the public page scannable
          // on mobile without hiding the value of the personal letter.
          const full = job.ai_explanation.trim();
          const firstBreak = full.search(/\n\s*\n|\*\*/);
          const cutAt = firstBreak > 60 && firstBreak < 220 ? firstBreak : 180;
          const isLong = full.length > cutAt + 40;
          const preview = isLong ? full.slice(0, cutAt).trimEnd() + "…" : full;
          return (
            <div className="card-default p-5">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <div className="label-overline">What this means for you</div>
                <span
                  className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-lime-400/20 border border-lime-400/40 text-[10px] font-bold uppercase tracking-wider text-lime-600 dark:text-lime-300"
                  data-testid="public-ai-new-pill"
                  title="Written by AI, just now, for you"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-lime-500 animate-pulse" />
                  AI · Written for you
                </span>
              </div>
              <div
                className="text-sm leading-relaxed mt-3"
                data-testid="public-ai-explanation"
              >
                {renderExplanation(aiExpanded || !isLong ? full : preview)}
              </div>
              {isLong && (
                <button
                  type="button"
                  onClick={() => setAiExpanded((v) => !v)}
                  className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                  data-testid="ai-explanation-toggle"
                >
                  {aiExpanded ? (
                    <>Show less <ChevronUp className="w-4 h-4" /></>
                  ) : (
                    <>Read full breakdown <ChevronDown className="w-4 h-4" /></>
                  )}
                </button>
              )}
            </div>
          );
        })()}

        {/* Items */}
        <div className="card-default overflow-hidden">
          <div className="p-5 border-b border-border">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="label-overline">Inspection report</div>
                <div className="font-display font-bold text-xl mt-1">Traffic-light grading</div>
              </div>
              <button
                type="button"
                onClick={() => setShowLegend((v) => !v)}
                className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1 shrink-0 mt-1"
                data-testid="legend-toggle"
              >
                What do the colours mean?
                {showLegend ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>
            {showLegend && (
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed" data-testid="legend-body">
                <span className="font-semibold text-destructive">Red</span> = urgent, recommended today.
                <span className="font-semibold text-warning"> Amber</span> = recommended within the next 3-6 months.
                <span className="font-semibold text-success"> Green</span> = inspected &amp; healthy.
              </p>
            )}
          </div>

          {(() => {
            const allItems = job.items || [];
            const accessoryItems = allItems.filter(i => i.type === "accessory");
            const nonAccessory = allItems.filter(i => i.type !== "accessory");
            const redItems   = nonAccessory.filter(i => i.urgency === "red");
            const amberItems = nonAccessory.filter(i => i.urgency === "amber");
            const greenItems = nonAccessory.filter(i => i.urgency === "green");

            // Inline section header + collapsible body, with friendly per-section copy.
            const Section = ({ id, items, urgency, title, intro, defaultOpen }) => {
              const isOpen = openSection[id] ?? defaultOpen;
              if (!items.length) return null;
              const colourCls =
                urgency === "red"   ? "border-destructive/30 bg-destructive/[0.04]" :
                urgency === "amber" ? "border-warning/30 bg-warning/[0.04]" :
                                       "border-success/30 bg-success/[0.04]";
              const chipCls =
                urgency === "red"   ? "bg-destructive/15 text-destructive" :
                urgency === "amber" ? "bg-warning/15 text-warning" :
                                       "bg-success/15 text-success";
              return (
                <div className={`border-b last:border-b-0 ${colourCls}`} data-testid={`section-${id}`}>
                  <button
                    type="button"
                    onClick={() => setOpenSection(o => ({ ...o, [id]: !isOpen }))}
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
                    data-testid={`section-${id}-toggle`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${chipCls}`}>
                        {urgency === "red" && <AlertTriangle className="w-3 h-3" />}
                        {items.length} {urgency}
                      </span>
                      <div className="min-w-0">
                        <div className="font-display font-bold text-base sm:text-lg leading-tight">{title}</div>
                        <div className="text-xs text-muted-foreground">{intro}</div>
                      </div>
                    </div>
                    {isOpen ? <ChevronUp className="w-4 h-4 flex-shrink-0 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 flex-shrink-0 text-muted-foreground" />}
                  </button>
                  {isOpen && (
                    <ul className="divide-y divide-border bg-card" data-testid={`section-${id}-body`}>
                      {items.map(renderItem)}
                    </ul>
                  )}
                </div>
              );
            };

            return (
              <div>
                <Section
                  id="red"
                  items={redItems}
                  urgency="red"
                  title="Urgent — please action today"
                  intro="Safety items that need doing before you drive again."
                  defaultOpen
                />
                <Section
                  id="amber"
                  items={amberItems}
                  urgency="amber"
                  title="Recommended — next 3-6 months"
                  intro="Advisories so you can plan ahead and budget."
                  defaultOpen
                />
                <Section
                  id="green"
                  items={greenItems}
                  urgency="green"
                  title="Passed inspection — no action needed"
                  intro={greenItems.length === 1 ? "1 item checked and given the all-clear." : `${greenItems.length} items checked and given the all-clear.`}
                  defaultOpen={false}
                />
                {accessoryItems.length > 0 && (
                  <div className="border-b last:border-b-0 border-primary/30 bg-primary/[0.04]" data-testid="section-accessories">
                    <div className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-primary/15 text-primary">
                          {accessoryItems.length} optional
                        </span>
                        <div>
                          <div className="font-display font-bold text-base sm:text-lg leading-tight">Make it yours — genuine accessories</div>
                          <div className="text-xs text-muted-foreground">Fitted while your car is with us — completely optional, no pressure.</div>
                        </div>
                      </div>
                    </div>
                    <ul className="divide-y divide-border bg-card" data-testid="section-accessories-body">
                      {accessoryItems.map((it) => {
                        const d = decisions[it.id];
                        return (
                          <li key={it.id} id={`item-${it.id}`} className={`p-5 transition-colors ${d === "approved" ? "bg-success/[0.06]" : ""}`} data-testid={`public-accessory-${it.id}`}>
                            <div className="flex items-start gap-3">
                              <div className="w-9 h-9 rounded-md grid place-items-center shrink-0 bg-primary/15 text-primary">
                                <Gift className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold">{it.name}</div>
                                {it.description && <div className="text-sm text-muted-foreground mt-1">{it.description}</div>}
                              </div>
                              <div className="font-mono-plex font-semibold shrink-0">£{(Number(it.quantity) * Number(it.unit_price || 0)).toFixed(2)}</div>
                            </div>
                            <div className="flex gap-2 mt-3 sm:pl-12">
                              <button
                                type="button"
                                onClick={() => setDecisions(prev => ({ ...prev, [it.id]: "approved" }))}
                                className={`flex-1 sm:flex-none sm:px-5 py-2 rounded-md text-sm font-semibold border transition-colors ${d === "approved" ? "bg-success text-white border-success" : "border-border hover:border-success/60 hover:text-success"}`}
                                data-testid={`accessory-yes-${it.id}`}
                              >
                                Yes — add to my visit
                              </button>
                              <button
                                type="button"
                                onClick={() => setDecisions(prev => ({ ...prev, [it.id]: "rejected" }))}
                                className={`flex-1 sm:flex-none sm:px-5 py-2 rounded-md text-sm font-semibold border transition-colors ${d === "rejected" ? "bg-muted text-foreground border-border" : "border-border hover:bg-muted/40"}`}
                                data-testid={`accessory-no-${it.id}`}
                              >
                                No thanks
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            );
          })()}
          <div className="p-5 bg-muted/30 border-t border-border space-y-3">
            {(job.booked_service_amount || 0) > 0 && (
              <div className="flex justify-between text-sm" data-testid="public-booked-row">
                <span className="text-muted-foreground">{job.booked_service_name || "Booked service"}</span>
                <span className="font-mono-plex">£{Number(job.booked_service_amount || 0).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Quoted extras subtotal</span>
              <span className="font-mono-plex">£{subtotal.toFixed(2)}</span>
            </div>
            {hasAnyDecision ? (
              <>
                {(job.booked_service_amount || 0) > 0 && (
                  <div className="flex justify-between text-sm" data-testid="public-extras-approved-row">
                    <span className="text-muted-foreground">Additional work you've approved</span>
                    <span className="font-mono-plex">£{approvedTotal.toFixed(2)}</span>
                  </div>
                )}
                {rebookTotal > 0 && (
                  <div className="flex justify-between text-sm" data-testid="public-extras-rebook-row">
                    <span className="text-primary inline-flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      Work you'd like rebooked for another day
                    </span>
                    <span className="font-mono-plex text-primary">£{rebookTotal.toFixed(2)}</span>
                  </div>
                )}
                {/* Applied bundle savings — one row per fired rule */}
                {((job.bundles?.applied) || []).map((b) => (
                  <div key={b.rule_id} className="flex justify-between text-sm" data-testid={`bundle-applied-${b.rule_id}`}>
                    <span className="text-success font-semibold inline-flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M2 7a2 2 0 012-2h12a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V7zm5 0a3 3 0 116 0v0a3 3 0 11-6 0zm4 0a1 1 0 10-2 0 1 1 0 002 0z"/></svg>
                      {b.type === "free_addon" ? "Free " + (b.name || "add-on") : b.name}
                    </span>
                    <span className="font-mono-plex text-success font-bold">
                      {b.type === "free_addon" ? `+ £${Number(b.discount || 0).toFixed(2)} value` : `– £${Number(b.discount || 0).toFixed(2)}`}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between items-baseline border-t border-border pt-3" data-testid="approving-total-row">
                  <span className="label-overline">
                    {(job.booked_service_amount || 0) > 0 ? "Total to pay on collection" : "You're approving"}
                  </span>
                  <span className="font-display font-bold text-2xl tracking-tight" data-testid="approved-total">
                    {/* Before submit, decisions are client-side only — compute live.
                        After submit the server response carries the authoritative grand_total. */}
                    £{Number(done
                      ? (job.grand_total ?? (Number(job.booked_service_amount || 0) + approvedTotal))
                      : Math.max(0, Number(job.booked_service_amount || 0) + approvedTotal - Number(job.bundles?.discount || 0))
                    ).toFixed(2)}
                  </span>
                </div>
                {/* "Almost there" nudges — items the customer can still add to unlock a bundle */}
                {((job.bundles?.eligible_progress) || []).map((p) => (
                  <div key={p.rule_id} className="rounded-md border border-primary/30 bg-primary/[0.05] p-2.5 mt-2"
                       data-testid={`bundle-nudge-${p.rule_id}`}>
                    <div className="text-xs font-bold text-primary">
                      💡 Almost there — {p.name}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      Approve {p.missing?.[0]?.qty_needed || 1} more {(p.missing?.[0]?.category || "").replace(/_/g, " ")} {p.missing?.[0]?.qty_needed > 1 ? "items" : "item"} to save up to £{Number(p.estimated_saving || 0).toFixed(2)}.
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="text-xs text-muted-foreground italic text-center pt-1" data-testid="approving-total-hint">
                Tap <strong className="not-italic text-success">Approve</strong>, <strong className="not-italic text-destructive">Decline</strong> or <strong className="not-italic text-primary">Speak to advisor</strong> on each item above — we'll total your approved work here.
              </div>
            )}
          </div>
        </div>

        {/* Comment */}
        <div className="card-default p-5">
          <label className="label-overline">A note for the workshop (optional)</label>
          <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} className="input-default mt-2" placeholder="Any questions or instructions?" data-testid="customer-comment-input" />
        </div>

        {/* Spread the cost — only when the customer has actually approved something */}
        {(Number(job.booked_service_amount || 0) + approvedTotal) > 0 && (
          <SpreadCostCard amount={Number(job.booked_service_amount || 0) + approvedTotal} />
        )}

        {(job.items || []).length === 0 ? (
          <div className="card-default p-5 text-center" data-testid="empty-quote-banner">
            <div className="label-overline">Inspection in progress</div>
            <p className="text-sm text-muted-foreground mt-2">
              Your technician is still working on the report. We'll text you the moment your quote is ready to review.
            </p>
          </div>
        ) : (
          <button onClick={submit} disabled={submitting || !hasAnyDecision} className="btn-primary w-full text-base py-3 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="submit-response-btn">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : !hasAnyDecision ? "Pick a decision on each item first" : <>Send my response <ArrowRight className="w-4 h-4" /></>}
          </button>
        )}

        <div className="text-center text-xs text-muted-foreground py-4">
          Powered by ServiceSell Pro · Secure customer authorisation
        </div>
      </main>

      {/* Tell my technician — floating button (always visible) */}
      <button
        type="button"
        onClick={() => { setShowTellTech(true); setTellTechSent(false); }}
        className="fixed bottom-5 right-5 md:bottom-6 md:right-6 z-30 inline-flex items-center gap-2 px-4 py-3 rounded-full bg-primary text-primary-foreground font-semibold shadow-2xl hover:scale-105 transition-transform"
        data-testid="tell-tech-fab"
        aria-label="Tell my technician"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="hidden sm:inline">Tell my technician</span>
      </button>

      {/* GDPR footer — visible to all customers */}
      {job.video_partner === "vehicle_visuals" && (
        <div className="max-w-4xl mx-auto px-5 mt-10 flex items-center gap-3 text-xs text-muted-foreground" data-testid="vv-partner-strip">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-[#263B91] text-white font-black text-[11px] tracking-tight">VV</span>
          <span>3D explainer animations on this quote are supplied by <strong className="text-foreground">Vehicle Visuals</strong> — <em>talk less, sell more</em>. Used at 15,000+ service locations worldwide.</span>
        </div>
      )}
      <GdprFooter token={token} />

      {/* Tell my technician modal */}
      {showTellTech && (
        <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" onClick={() => setShowTellTech(false)} data-testid="tell-tech-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border w-full sm:max-w-md rounded-t-2xl sm:rounded-lg overflow-hidden animate-fade-up">
            {!tellTechSent ? (
              <>
                <div className="flex items-start justify-between p-5 border-b border-border">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/15 grid place-items-center shrink-0">
                      <MessageCircle className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <div className="label-overline">Quick message</div>
                      <h3 className="font-display font-bold text-lg mt-0.5 leading-tight">Tell your technician</h3>
                      <p className="text-xs text-muted-foreground mt-1">Forgot something? A noise, a smell, a question — anything goes. Goes straight to the workshop.</p>
                    </div>
                  </div>
                  <button onClick={() => setShowTellTech(false)} className="btn-outline" data-testid="tell-tech-close-btn"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-3">
                  <textarea
                    value={tellTechText}
                    onChange={(e) => setTellTechText(e.target.value)}
                    rows={5}
                    maxLength={1000}
                    placeholder="e.g. I forgot to mention there's a squeak from the front when I brake at low speeds."
                    className="input-default text-sm"
                    autoFocus
                    data-testid="tell-tech-input"
                  />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-mono-plex text-muted-foreground">{tellTechText.length} / 1000</span>
                    <button
                      type="button"
                      onClick={sendTellTech}
                      disabled={tellTechBusy || !tellTechText.trim()}
                      className="btn-primary text-sm"
                      data-testid="tell-tech-send-btn"
                    >
                      {tellTechBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Send
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-8 text-center" data-testid="tell-tech-sent">
                <div className="w-12 h-12 rounded-full bg-success text-success-foreground grid place-items-center mx-auto">
                  <Check className="w-6 h-6" />
                </div>
                <h3 className="font-display font-bold text-xl mt-4">Got it — your technician will see it.</h3>
                <p className="text-sm text-muted-foreground mt-1.5">Send another, or close this window.</p>
                <div className="mt-5 flex gap-2 justify-center">
                  <button type="button" onClick={() => { setTellTechSent(false); }} className="btn-outline text-sm" data-testid="tell-tech-another-btn">
                    Send another
                  </button>
                  <button type="button" onClick={() => setShowTellTech(false)} className="btn-primary text-sm" data-testid="tell-tech-done-btn">
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Safety warning modal */}
      {warning && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" data-testid="safety-warning-modal">
          <div className="bg-card rounded-lg border-2 border-destructive shadow-xl w-full max-w-md overflow-hidden animate-fade-up">
            <div className="relative h-40">
              <img src="https://images.unsplash.com/photo-1760317890322-364a810cd4da" alt="Safety" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-destructive/80 to-destructive/30" />
              <div className="absolute bottom-3 left-4 right-4 text-white">
                <div className="flex items-center gap-2"><AlertTriangle className="w-5 h-5" /><span className="label-overline text-white/90">Red — urgent</span></div>
                <div className="font-display font-bold text-xl mt-1">A quick word about {warning.item.name}</div>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm leading-relaxed" data-testid="safety-warning-pitch">{warning.pitch}</p>
              {warning.ai_pending && (
                <div className="flex items-center gap-2 mt-2 text-[11px] text-muted-foreground" data-testid="safety-warning-ai-pending">
                  <Loader2 className="w-3 h-3 animate-spin" /> personalising for your vehicle…
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 mt-5">
                <button onClick={() => { setWarning(null); }} className="btn-outline" data-testid="warning-keep-rejection-btn">
                  Keep declined
                </button>
                <button onClick={() => { setDecisions(prev => ({ ...prev, [warning.item.id]: "approved" })); setWarning(null); }} className="btn-primary" data-testid="warning-reconsider-btn">
                  Reconsider
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
